import { api } from './api.js';
import { toast } from '../stores/toastStore.js';

const SYNC_OK_KEY = 'cb8:_sync_ok';
const SYNC_ERROR_KEY = 'cb8:_sync_error';
const SYNC_META_KEY = 'cb8:_sync_meta';
const SYNC_TOAST_WINDOW_MS = 30_000;
export const CLIENT_ACCOUNT_KEY = 'karat:client-account';
export const ANONYMOUS_PLAN_KEY = 'karat:anonymous-plan';
const PENDING_ANONYMOUS_ACCOUNT_KEY = 'karat:anonymous-plan-account';
const ANONYMOUS_PLAN_VERSION = 1;
const ANONYMOUS_PLAN_STORE_KEYS = ['karat-finance'];

const ACCOUNT_STORAGE_PREFIXES = ['karat-', 'karat:', 'cb8:'];
const ACCOUNT_STORAGE_KEYS = new Set(['merchant-memory-v1']);

// COPY-HOOK
const SYNC_ERROR_MESSAGE = "Couldn't sync to cloud — your changes are saved locally and will retry";

let lastSyncErrorToastAt = 0;
let activeAccountId = null;

function notifySyncError() {
  const now = Date.now();
  if (now - lastSyncErrorToastAt < SYNC_TOAST_WINDOW_MS) return;
  lastSyncErrorToastAt = now;
  toast.error(SYNC_ERROR_MESSAGE);
}

function browserStorage(name) {
  try {
    return globalThis[name] || null;
  } catch {
    return null;
  }
}

function storageGet(key) {
  try {
    return browserStorage('localStorage')?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    const storage = browserStorage('localStorage');
    if (!storage) return false;
    storage.setItem(key, value);
    return true;
  } catch {
    // The in-memory store still works when browser storage is unavailable.
    return false;
  }
}

function storageRemove(key) {
  try {
    browserStorage('localStorage')?.removeItem(key);
  } catch {
    // Browser storage is optional; callers retain their in-memory state.
  }
}

function readAnonymousPlanMarker() {
  try {
    const marker = JSON.parse(storageGet(ANONYMOUS_PLAN_KEY) || 'null');
    return marker?.version === ANONYMOUS_PLAN_VERSION ? marker : null;
  } catch {
    return null;
  }
}

function isAccountStorageKey(key) {
  return (
    ACCOUNT_STORAGE_KEYS.has(key) ||
    ACCOUNT_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))
  );
}

function hasAccountEntries(storage) {
  if (!storage) return false;

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key !== CLIENT_ACCOUNT_KEY && isAccountStorageKey(key)) {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

function removeAccountEntries(storage) {
  if (!storage) return 0;

  try {
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && isAccountStorageKey(key)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
    return keys.length;
  } catch {
    return 0;
  }
}

/**
 * Remove only Karat-owned browser state. The app shares erebora.org storage
 * with other paths, so an origin-wide clear would erase unrelated products.
 */
export function clearClientAccountState() {
  // WHY: localStorage is origin-scoped, not path-scoped. Keep this allowlist
  // boundary so logout cannot erase another erebora.org application's data.
  const localCleared = removeAccountEntries(browserStorage('localStorage'));
  const sessionCleared = removeAccountEntries(browserStorage('sessionStorage'));
  activeAccountId = null;
  return { localCleared, sessionCleared };
}

/**
 * Start or resume the one explicitly marked pre-account plan.
 */
export function prepareAnonymousPlanState() {
  const marker = readAnonymousPlanMarker();
  const accountId = storageGet(CLIENT_ACCOUNT_KEY);
  if (marker && !accountId) return { resumed: true };

  const hadAccountState = (
    !!accountId ||
    hasAccountEntries(browserStorage('localStorage')) ||
    hasAccountEntries(browserStorage('sessionStorage'))
  );
  // WHY: a signed-out browser can still hold an expired or foreign account
  // cache. A new anonymous plan must start from empty Karat state.
  clearClientAccountState();
  storageSet(ANONYMOUS_PLAN_KEY, JSON.stringify({
    version: ANONYMOUS_PLAN_VERSION,
    startedAt: new Date().toISOString(),
  }));
  return { resumed: false, reloadRequired: hadAccountState };
}

/**
 * Record the exact authenticated account allowed to claim the marked plan.
 */
export function authorizeAnonymousPlanBinding(userId) {
  const nextAccountId = typeof userId === 'string' ? userId.trim() : '';
  if (!nextAccountId || !readAnonymousPlanMarker()) return false;
  storageSet(PENDING_ANONYMOUS_ACCOUNT_KEY, nextAccountId);
  return true;
}

export function hasPendingAnonymousPlanForAccount(userId) {
  return (
    !!readAnonymousPlanMarker() &&
    typeof userId === 'string' &&
    storageGet(PENDING_ANONYMOUS_ACCOUNT_KEY) === userId
  );
}

/**
 * Bind the current browser cache to one authenticated account.
 *
 * A different marker is a hard account boundary. Unmarked state is adopted
 * only during an already-authenticated bootstrap; an explicit login purges it
 * because its prior owner cannot be proven.
 */
export function activateClientAccount(userId, { allowUnownedState = false } = {}) {
  const nextAccountId = typeof userId === 'string' ? userId.trim() : '';
  if (!nextAccountId) {
    return { accountChanged: false, claimedAnonymousState: false };
  }

  const storedAccountId = storageGet(CLIENT_ACCOUNT_KEY);
  const previousAccountId = storedAccountId || activeAccountId;
  const hasUnownedState = (
    previousAccountId == null &&
    (
      hasAccountEntries(browserStorage('localStorage')) ||
      hasAccountEntries(browserStorage('sessionStorage'))
    )
  );
  const accountChanged = (
    (previousAccountId != null && previousAccountId !== nextAccountId) ||
    (hasUnownedState && !allowUnownedState)
  );

  if (accountChanged) clearClientAccountState();

  activeAccountId = nextAccountId;
  storageSet(CLIENT_ACCOUNT_KEY, nextAccountId);
  return {
    accountChanged,
    claimedAnonymousState: previousAccountId == null && !accountChanged,
  };
}

function readSyncMeta() {
  try {
    return JSON.parse(storageGet(SYNC_META_KEY) || '{}');
  } catch {
    return {};
  }
}

function updateSyncMeta(key, patch) {
  const meta = readSyncMeta();
  meta[key] = { ...(meta[key] || {}), ...patch };
  storageSet(SYNC_META_KEY, JSON.stringify(meta));
}

function cacheValue(key, value, updatedAt) {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) return;
  storageSet(key, serialized);
  if (updatedAt) {
    updateSyncMeta(key, {
      dirty: false,
      updatedAt,
    });
  }
}

function storeRows(payload) {
  const source = payload?.items ?? payload?.data ?? payload?.store ?? payload;
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== 'object') return [];

  return Object.entries(source).map(([key, entry]) => {
    if (entry && typeof entry === 'object' && 'key' in entry) return entry;
    if (entry && typeof entry === 'object' && 'value' in entry) {
      return { key, ...entry };
    }
    return { key, value: entry };
  });
}

function recordSyncFailure(key, error) {
  const message = error?.message || String(error);
  console.warn('Cloud sync failed for', key, message);
  storageSet(SYNC_ERROR_KEY, JSON.stringify({
    key,
    error: message,
    ts: Date.now(),
  }));
  notifySyncError();
}

async function putStoreValue(key, value) {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError(`Cannot sync undefined store value for ${key}`);
  }

  const result = await api(`/store/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: { value },
  });
  if (storageGet(key) === serialized) {
    updateSyncMeta(key, {
      dirty: false,
      updatedAt: result?.updated_at || new Date().toISOString(),
    });
  }
  storageSet(SYNC_OK_KEY, String(Date.now()));
  return result;
}

/**
 * Push only the marked first-session plan after /auth/me proves the fresh
 * cookie-backed account. No login path calls this function.
 */
export async function bindAnonymousPlanToAccount(userId) {
  if (!hasPendingAnonymousPlanForAccount(userId)) {
    throw new Error('Anonymous plan is not authorized for this account');
  }

  const values = ANONYMOUS_PLAN_STORE_KEYS.map((key) => {
    const raw = storageGet(key);
    if (raw == null) return null;
    try {
      return { key, value: JSON.parse(raw) };
    } catch {
      throw new Error(`Anonymous plan store is invalid: ${key}`);
    }
  }).filter(Boolean);
  if (values.length === 0) {
    throw new Error('Anonymous plan has no money data to save');
  }

  const account = activateClientAccount(userId, { allowUnownedState: true });
  if (account.accountChanged) {
    throw new Error('Anonymous plan crossed an account boundary');
  }

  for (const entry of values) {
    await putStoreValue(entry.key, entry.value);
  }

  storageRemove(ANONYMOUS_PLAN_KEY);
  storageRemove(PENDING_ANONYMOUS_ACCOUNT_KEY);
  return { pushed: values.length };
}

/**
 * Read browser storage first, then fill a cache miss from the account store.
 */
export async function sGet(key) {
  const localValue = storageGet(key);
  if (localValue != null) {
    try {
      return JSON.parse(localValue);
    } catch {
      // A corrupt entry is treated as a miss so the server copy can repair it.
    }
  }

  try {
    const row = await api(`/store/${encodeURIComponent(key)}`);
    if (!row || !Object.prototype.hasOwnProperty.call(row, 'value')) return null;
    cacheValue(key, row.value, row.updated_at);
    return row.value;
  } catch {
    return null;
  }
}

/**
 * Commit to browser storage and mark the value dirty before a cloud attempt.
 */
function cacheDirtyValue(key, value) {
  const serialized = JSON.stringify(value);
  if (serialized !== undefined) storageSet(key, serialized);
  updateSyncMeta(key, {
    dirty: true,
    localUpdatedAt: new Date().toISOString(),
  });
}

function hasActiveAccountBinding() {
  return !(
    typeof activeAccountId !== 'string' ||
    activeAccountId.length === 0 ||
    storageGet(CLIENT_ACCOUNT_KEY) !== activeAccountId
  );
}

/**
 * Persist a value only when the current authenticated account owns this cache.
 * Unlike ordinary autosave, the caller receives a rejection when the server
 * does not acknowledge the write.
 */
export async function sSetRequired(key, value) {
  cacheDirtyValue(key, value);
  if (!hasActiveAccountBinding()) {
    throw new Error('Authenticated account storage is not active');
  }

  try {
    return await putStoreValue(key, value);
  } catch (error) {
    recordSyncFailure(key, error);
    throw error;
  }
}

/**
 * Commit to browser storage synchronously, then best-effort PUT the same value.
 */
export async function sSet(key, value) {
  cacheDirtyValue(key, value);

  if (!hasActiveAccountBinding()) {
    // WHY: anonymous and signed-out Zustand persistence is local-only. The
    // explicit account activation/binding is the sole authority for a cloud
    // write, so an existing-email response cannot upload an unowned plan.
    return;
  }

  try {
    await putStoreValue(key, value);
  } catch (error) {
    recordSyncFailure(key, error);
  }
}

/**
 * Pull the authenticated user's KV rows into browser storage on login.
 */
export async function syncFromCloud(userId, options) {
  if (!userId) return { pulled: 0 };

  const {
    claimAnonymousPlan = false,
    ...accountOptions
  } = options || {};
  const binding = claimAnonymousPlan
    ? await bindAnonymousPlanToAccount(userId)
    : null;

  const unboundAnonymousPlan = (
    !claimAnonymousPlan &&
    readAnonymousPlanMarker() != null
  );
  const account = activateClientAccount(userId, {
    ...accountOptions,
    // WHY: a magic or returning-cookie bootstrap may adopt genuinely
    // unmarked offline state, but an anonymous plan belongs to nobody until
    // its explicit pending-account marker authorizes the claim.
    allowUnownedState:
      accountOptions.allowUnownedState === true && !unboundAnonymousPlan,
  });
  if (account.accountChanged) {
    return { pulled: 0, accountChanged: true };
  }

  try {
    const payload = await api('/store');
    const rows = storeRows(payload);
    const meta = readSyncMeta();
    let pulled = 0;

    for (const row of rows) {
      if (!row || typeof row.key !== 'string' || !('value' in row)) continue;

      const localMeta = meta[row.key];
      const localTime = Date.parse(localMeta?.localUpdatedAt || localMeta?.updatedAt || '');
      const cloudTime = Date.parse(row.updated_at || '');
      const keepDirtyLocal = (
        storageGet(row.key) != null &&
        (
          localMeta?.dirty === true ||
          (Number.isFinite(localTime) && Number.isFinite(cloudTime) && localTime > cloudTime)
        )
      );

      if (keepDirtyLocal) {
        try {
          await sSet(row.key, JSON.parse(storageGet(row.key)));
        } catch {
          // sSet records its own retry receipt.
        }
        continue;
      }

      cacheValue(row.key, row.value, row.updated_at);
      pulled += 1;
    }

    return {
      pulled,
      ...(binding ? { anonymousPlanPushed: binding.pushed } : {}),
    };
  } catch (error) {
    console.warn('Cloud sync on login failed, using local data:', error?.message || error);
    return { pulled: 0 };
  }
}

if (typeof window !== 'undefined') {
  window.__karatSync = { syncFromCloud, sGet, sSet };
}
